import {
  Avatar,
  Badge,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  useBreakpointValue,
  useDisclosure,
  useToken,
} from '@chakra-ui/react';
import { ReactElement, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { AngleDown } from '@/client/components/icons/individual/AngleDown';
import { Bell } from '@/client/components/icons/individual/Bell';
import { CircleCheck } from '@/client/components/icons/individual/CircleCheck';
import { ExternalLink } from '@/client/components/icons/individual/ExternalLink';
import { Language } from '@/client/components/icons/individual/Language';
import { Logout } from '@/client/components/icons/individual/Logout';
import { Qooper } from '@/client/components/icons/individual/Qooper';
import { QuestionCircle } from '@/client/components/icons/individual/QuestionCircle';
import { Star } from '@/client/components/icons/individual/Star';
import SupportModal from '../overlay/SupportModal';
import { UserEdit } from '@/client/components/icons/individual/UserEdit';
import UserNotificationsDrawer from '../overlay/UserNotificationsDrawer';
import { UserSearch } from '@/client/components/icons/individual/UserSearch';
import { hexToRGBA } from '@/client/utils/hexToRGBA';
import { useAccessHelper } from '@/client/services/hooks/auth/useAccessHelper';
import { useAuthStore } from '@/client/services/state/authStore';
import { useEndSession } from '@/client/services/hooks/auth/useEndSession';
import { useConfigStore } from '@/client/services/state/configStore';
import { useConsentStore } from '@/client/services/state/consentStore';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import v3ApiService from '../../services/api/clients/v3ApiClient';

type MenuOption = {
  label: string;
  icon: any;
  onClick: () => void;
  render: boolean;
  rightElement?: ReactElement;
};

export default function UserMenu() {
  const { t } = useTranslation();
  const { authConfig } = useAuthStore();
  const { user, company } = authConfig;
  const endSession = useEndSession();
  const openConsentPreferences = useConsentStore((s) => s.openPreferences);
  const navigate = useNavigate();
  const { allowance } = useAccessHelper();
  const { config } = useConfigStore();
  const queryClient = useQueryClient();
  const { onClose } = useDisclosure();
  const [notificationsOpen, setNotificationsOpen] = useState<boolean>(false);
  const [supportModalOpen, setSupportModalOpen] = useState<boolean>(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [brandButtonColor] = useToken('colors', ['brand.button']);
  const hoverColor = hexToRGBA(brandButtonColor, 0.1);

  const { data: unreadNotifications } = useQuery({
    queryKey: ['notifications', user._id],
    queryFn: () => v3ApiService.getUserNotifications(user._id, false),
  });

  const { data: readNotifications } = useQuery({
    queryKey: ['notifications', user._id, true],
    queryFn: () => v3ApiService.getUserNotifications(user._id, true),
  });

  const hubLinks: MenuOption[] = useMemo(() => {
    if (!config.hubLinks || config.hubLinks.length === 0) return [];

    return config.hubLinks.map((link) => ({
      label: link.text,
      icon: ExternalLink,
      onClick: () => window.open(link.url, '_blank'),
      render: true,
    }));
  }, [config.hubLinks]);

  const menuListOptions: MenuOption[] = useMemo(
    () => [
      {
        label: t('userMenu.notifications'),
        icon: Bell,
        rightElement:
          unreadNotifications && unreadNotifications.length > 0 ? (
            <Badge
              backgroundColor="red.300"
              color="white"
              fontSize="12px"
              borderRadius="full"
              minWidth="16px"
              height="16px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              paddingX={1}
            >
              {unreadNotifications?.length}
            </Badge>
          ) : undefined,
        onClick: () => setNotificationsOpen(true),
        render: true,
      },
      {
        label: t('userMenu.editProfile'),
        icon: UserEdit,
        onClick: () => navigate('/profile'),
        render: true,
      },
      {
        label: t('userMenu.peopleSearch'),
        icon: UserSearch,
        onClick: () => navigate('/people'),
        render: !allowance.inPartner && company.feature_flags.enable_explore_search,
      },
      {
        label: t('userMenu.support'),
        icon: QuestionCircle,
        onClick: () => setSupportModalOpen(true),
        render: true,
      },
      {
        label: t('userMenu.whatsNew'),
        icon: Star,
        onClick: () => window.open('https://product.continu.com/changelog', '_blank'),
        render: company.feature_flags.show_whatsnew,
      },
      {
        label: t('userMenu.roadmap'),
        icon: CircleCheck,
        onClick: () =>
          window.open(
            `https://product.continu.com/roadmap?email=${encodeURIComponent(user.email)}`,
            '_blank',
          ),
        render: true,
      },
      {
        label: t('userMenu.reviewUs'),
        icon: Language,
        onClick: () => window.open('https://www.continu.com/lp/review-us', '_blank'),
        render: company.feature_flags.show_referral,
      },
      ...hubLinks,
      {
        label: t('userMenu.qooper'),
        icon: Qooper,
        onClick: () => navigate('/mentor'),
        render: !allowance.inPartner && company.feature_flags.enable_qooper,
      },
      {
        label: t('userMenu.cookiePreferences'),
        icon: Language,
        onClick: () => openConsentPreferences(),
        render: !!config?.features?.consent_management_enabled,
      },
      {
        label: t('userMenu.logout'),
        icon: Logout,
        onClick: () => {
          localStorage.removeItem('redirectLocation');
          localStorage.removeItem('preRedirect');
          queryClient.clear();
          onClose();
          endSession();
        },
        render: true,
      },
    ],
    [unreadNotifications, company, hubLinks, allowance],
  );

  return (
    <Flex data-testid="user-menu">
      <Menu>
        <MenuButton
          aria-label="User Menu"
          color="neutral.1500"
          fontSize="14px"
          fontWeight={600}
          padding={4}
          borderRadius="8px"
          _hover={{
            backgroundColor: hoverColor,
          }}
        >
          <HStack spacing={2}>
            <Avatar size="sm" name={user.full_name} src={user.image} position="relative">
              {unreadNotifications && unreadNotifications.length > 0 && (
                <Badge
                  position="absolute"
                  backgroundColor="red.300"
                  color="white"
                  zIndex={9999}
                  top={-2}
                  left="60%"
                  borderRadius="full"
                  fontSize="10px"
                  minWidth="16px"
                  height="16px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  paddingX={1}
                >
                  {unreadNotifications.length}
                </Badge>
              )}
            </Avatar>

            {!isMobile && (
              <>
                <Text>{user.full_name}</Text>

                <AngleDown boxSize={3} />
              </>
            )}
          </HStack>
        </MenuButton>

        <Portal>
          <MenuList
            paddingX={2}
            paddingY={4}
            backgroundColor="neutral.0"
            border="1px solid"
            borderColor="neutral.100"
            borderRadius="8px"
          >
            {menuListOptions.map((option) => {
              if (!option.render) return null;

              return (
                <MenuItem
                  key={option.label}
                  fontSize="14px"
                  fontWeight={500}
                  color="baseBlack"
                  backgroundColor="neutral.0"
                  paddingX={4}
                  paddingY={3}
                  borderRadius="8px"
                  onClick={option.onClick}
                  _hover={{
                    backgroundColor: hoverColor,
                  }}
                >
                  <HStack spacing={2}>
                    <Icon as={option.icon} boxSize={4} />

                    <Text>{option.label}</Text>

                    {option.rightElement}
                  </HStack>
                </MenuItem>
              );
            })}
          </MenuList>
        </Portal>
      </Menu>

      <UserNotificationsDrawer
        onClose={() => {
          setNotificationsOpen(false);
          onClose();
        }}
        isOpen={notificationsOpen}
        unreadNotifications={unreadNotifications}
        readNotifications={readNotifications}
      />

      <SupportModal
        onClose={onClose}
        supportModalOpen={supportModalOpen}
        setSupportModalOpen={setSupportModalOpen}
      />
    </Flex>
  );
}
